import ConsumerBonusService from "@api/consumer/consumerBonusService";
import { useRole } from "@hooks/userHooks";
import {
    BonusCodeAccess, BonusCodeType,
    ConsumerBonusCodeFull,
} from "@interfaces/consumer/bonusCodes";
import { fromUtcDate } from "@services/helpers";
import {FC} from "react";
import { toastr } from "@/utils/toastHelper";
import {BonusCodeFormValues} from "./forms"
import BonusCodeForm from "./bonusCodeForm";
import * as Yup from "yup";
import {ObjectSchema} from "yup";

interface ConsumerBonusCodeInfoProps {
    code: ConsumerBonusCodeFull;
}

const EditBonusCodeForm: FC<ConsumerBonusCodeInfoProps> = ({ code }) => {

    const value = JSON.parse(code.valueString);
    const itemId = value?.ItemId;
    const itemCount = value?.Count;
    const balanceValue = value?.Value;

    const initial: BonusCodeFormValues = {
        code: code.code,
        count: code.useCount,
        maxUseCount: code.maxUseCount,
        timeLimited: Boolean(code.beginTime || code.endTime),
        isActive: code.isActive,
        beginTime: (code.beginTime && fromUtcDate(code.beginTime).toDate()) || new Date(),
        endTime: (code.endTime && fromUtcDate(code.endTime).toDate()) || new Date(),
        access: code.access,

        itemId: itemId || "",
        itemCount: itemCount || 1,
        money: balanceValue || 0,
        type: code.type
    }

    const canEdit = useRole('consumer.bonus.edit');

    const onSubmit = async (values: BonusCodeFormValues) => {

        if (!canEdit) {
            return;
        }

        const api = new ConsumerBonusService();
        const result = await api.patchBonusCode({
            id: code.id,
            isActive: values.isActive,
            code: values.code ?? '',
            maxUseCount: values.maxUseCount,
            beginTime: values.timeLimited ? values.beginTime?.toUTCString() : undefined,
            endTime: values.timeLimited ? values.endTime?.toUTCString() : undefined
        });

        if (result.success) {
            toastr.success("Бонусный код", "Код успешно изменен");
        }
        else {
            if (result.errorCode === 'CodeNotFound') {
                toastr.error('Бонусный код', 'Код не найден');
            }
            else {
                toastr.error('Бонусный код', 'Что-то пошло не так');
            }
        }
    };

    const validationSchema: ObjectSchema<BonusCodeFormValues> = Yup.object({
        code: Yup.string()
            .min(3)
            .max(36)
            .required(),
        count: Yup.number(),
        maxUseCount: Yup.number()
            .min(0)
            .max(10000)
            .required(),

        timeLimited: Yup.boolean().required(),
        beginTime: Yup.date(),
        endTime: Yup.date(),

        isActive: Yup.boolean().required(),
        access: Yup.mixed<BonusCodeAccess>()
            .oneOf(Object.values(BonusCodeAccess))
            .required(),

        type: Yup.mixed<BonusCodeType>()
            .oneOf(Object.values(BonusCodeType))
            .required(),
        itemId: Yup.string()
            .when("type", {
                is: BonusCodeType.Item,
                then: schema => schema.required()
            }),
        itemCount: Yup.number()
            .when("type", {
                is: BonusCodeType.Item,
                then: schema => schema
                    .min(1)
                    .max(2000000000)
                    .required()
            }),
        money: Yup.number()
            .when("type", {
                is: BonusCodeType.Balance,
                then: schema => schema
                    .min(1)
                    .max(100000)
                    .required()
            })
    })

    return (
        <BonusCodeForm
            initial={initial}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
            editForm />
    )
}

export default EditBonusCodeForm;